import axe from '@axe-core/react';
import { isEmpty } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import Modal from 'react-modal';
import { Provider } from 'react-redux';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { Store } from 'redux';

import { characterEnvActions, Constants, ConfigUtils, generateApiAdapter } from '@dndbeyond/character-rules-engine/es';
import { getMessageBroker, IMessageBroker } from '@dndbeyond/message-broker-lib';

import config from '~/config';
import useUserId from '~/hooks/useUserId';
import useUserName from '~/hooks/useUserName';

import '../styles/styles.scss';
import CharacterBuilderContainer from './CharacterBuilder/containers/CharacterBuilderContainer';
import configureBuilderStore from './CharacterBuilder/stores/configureStore';
import CharacterSheetContainer from './CharacterSheet/containers/CharacterSheetContainer';
import configureSheetStore from './CharacterSheet/store/configureStore';
import { appEnvActions } from './Shared/actions';
import { Managers } from './Shared/managers';
import { DiceFeatureConfigurationState, GameLogState } from './Shared/stores/typings';
import { AppLoggerUtils, AppNotificationUtils, ErrorUtils } from './Shared/utils';
import appConfig from './config';

const BASE_PATHNAME = config.basePathname;

interface Props {
    readOnly: boolean;
    redirect?: string;
    characterId: number | null;
}

export const SheetBuilderApp: React.FC<Props> = ({ readOnly, characterId, redirect }) => {
    const username = useUserName();
    const userId = useUserId();
    const navigate = useNavigate();
    const matchBuilderWithOutCharacter = useMatch(`${BASE_PATHNAME}/builder/*`);
    const matchBuilderWithCharacter = useMatch(`${BASE_PATHNAME}/:characterId/builder/*`);
    const matchSheet = useMatch(`${BASE_PATHNAME}/:characterId/`);
    const matchSheetWithShareId = useMatch(`${BASE_PATHNAME}/:characterId/:shareId`);
    const currentPath = useLocation().pathname;

    if (config === undefined) {
        throw Error('Missing Config');
    }

    let appContext: Constants.AppContextTypeEnum | null = null;
    switch (true) {
        case !!(matchBuilderWithOutCharacter || matchBuilderWithCharacter):
            appContext = Constants.AppContextTypeEnum.BUILDER;
            break;
        case !!(matchSheet || matchSheetWithShareId):
            appContext = Constants.AppContextTypeEnum.SHEET;
            break;
        default:
            throw new Error('Invalid app context');
    }

    const diceFeatureConfiguration: DiceFeatureConfigurationState = {
        enabled: true,
        menu: true,
        assetBaseLocation: config.diceAssetEndpoint,
        apiEndpoint: config.diceApiEndpoint,
        trackingId: config.analyticTrackingId,
    };

    ErrorUtils.initReporting({
        dsn: appConfig.sentryUrl,
        environment: config.env ? config.env : 'unknown',
        debug: appConfig.debug,
        release: appConfig.version ? appConfig.version : 'unknown',
    });

    let store: Store;
    if (appContext === Constants.AppContextTypeEnum.BUILDER) {
        store = configureBuilderStore();
        store.dispatch({ type: 'SET_ROUTE_CONTROLS', payload: { navigate, currentPath } });
    } else {
        store = configureSheetStore();
    }

    let parsedDiceEnabled: boolean = true;
    if (!diceFeatureConfiguration.enabled) {
        parsedDiceEnabled = false;
    } else {
        // if local storage is disabled it will just crash when trying to access, this prevents crashing.
        try {
            const diceEnabled = localStorage.getItem('dice-enabled');
            // once dice are enabled set the default to on;
            parsedDiceEnabled = diceEnabled === 'true' || diceEnabled === null;
        } catch (e) {}
    }

    let lastMessageTime = 0;
    try {
        const lsLastMessageTime = localStorage.getItem(`gameLog-lastMessageTime-${characterId}`);
        if (lsLastMessageTime) {
            lastMessageTime = parseInt(lsLastMessageTime);
        }
    } catch (e) {}

    const gameLogSettings: GameLogState = {
        isOpen: false,
        lastMessageTime: lastMessageTime,
        apiEndpoint: config.gameLogApiEndpoint,
    };

    ConfigUtils.configureRulesEngine({
        apiAdapter: generateApiAdapter(config.authEndpoint, readOnly),
        apiInfo: config.apiInfo,
        store,
        onLogMessage: AppLoggerUtils.logMessage,
        onLogError: AppLoggerUtils.logError,
        onNotification: AppNotificationUtils.dispatchNotification,
        includeCustomItems: true,
        canUseCurrencyContainers: true,
    });

    store.dispatch(
        characterEnvActions.dataSet({
            context: appContext,
        }),
    );

    store.dispatch(
        appEnvActions.dataSet({
            username,
            userId: userId ? Number(userId) : -1,
            characterId,
            authEndpoint: config.authEndpoint,
            redirect,
            diceEnabled: parsedDiceEnabled,
            diceFeatureConfiguration: diceFeatureConfiguration,
            gameLog: gameLogSettings,
        }),
    );

    // Setup axe-core in development, not in production
    if (process.env.NODE_ENV !== 'production') {
        axe(React, ReactDOM, 10000);
    }

    Modal.setAppElement('#character-tools-target');
    return (
        <Provider store={store}>
            <Managers>
                {matchBuilderWithOutCharacter || matchBuilderWithCharacter ? (
                    <CharacterBuilderContainer />
                ) : (
                    <CharacterSheetContainer />
                )}
            </Managers>
        </Provider>
    );
};
